import { Button, Modal } from '@buildbox/components'
import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import React, { useCallback, useEffect, useState } from 'react'
import { IBanner } from 'shared/interfaces/banner'
import { updateBannerOrder } from 'shared/services/banner.service'
import { primaryDashboardButtonStyles } from 'shared/styles/theme'
import { SortableItem } from './sortableItem'
import { Container } from './styles'
import { IProps } from './types'
import {
	restrictToVerticalAxis,
	restrictToWindowEdges,
} from '@dnd-kit/modifiers'
import cogoToast from 'cogo-toast'
import cogoDefaultOptions from 'shared/util/toaster'

function ModalOrderBanner(props: IProps) {
	const { isActive, closeModal, banners, selectedRetail } = props

	const [items, setItems] = useState<IBanner[]>(banners)
	const [newOrder, setNewOrder] = useState<
		{ _id: string; order: number; retailId: string }[]
	>([])

	const refetchData = true

	useEffect(() => {
		const bannersSorted = banners.sort(
			(a, b) =>
				(a.retail.find((retail) => retail.retailId === selectedRetail.value)
					?.order ?? 0) -
				(b.retail.find((retail) => retail.retailId === selectedRetail.value)
					?.order ?? 0),
		)
		setItems(bannersSorted)
	}, [banners])

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	)

	function handleDragEnd(event: any) {
		const { active, over }: { active: any; over: any } = event

		const changedOrder = active.id !== over.id

		if (changedOrder) {
			setItems((items) => {
				const oldIndex = items.findIndex((item) => item._id === active.id)
				const newIndex = items.findIndex((item) => item._id === over.id)

				return arrayMove(items, oldIndex, newIndex)
			})
		}
	}

	useEffect(() => {
		const newOrder = items.map((item, index) => ({
			_id: item._id,
			order: index,
			retailId: selectedRetail.value,
		}))

		setNewOrder(newOrder)
	}, [items, selectedRetail])

	const onClose = useCallback(
		(refetchData: boolean = false) => {
			closeModal({ refetchData, dataToFetch: ['banner'] })
		},
		[closeModal],
	)

	async function onSaveOrder() {
		try {
			await updateBannerOrder(newOrder)
		} catch (error) {
			console.error('::: error :::', error)
		} finally {
			onClose(refetchData)
			cogoToast.success('Banner reordenado com sucesso', cogoDefaultOptions)
		}
	}

	function onCloseModal() {
		onClose(refetchData)
		setItems([])
		setNewOrder([])
	}

	return (
		<Modal
			isActive={isActive}
			title='Reordenar Banners'
			onClose={onCloseModal}
			className='modal-banner'
			size={600}
		>
			<Container>
				<div className='banner-order-list'>
					<div className='banner-order-header'>
						<span className='position-column'>#</span>
						<span>Capa</span>
						<span>Título</span>
						<span className='register-column-header'>Data de atualização</span>
						<span></span>
					</div>
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
						modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
					>
						<SortableContext
							items={items.map((item) => item._id)}
							strategy={verticalListSortingStrategy}
						>
							{items.map((item, index) => (
								<SortableItem
									key={item._id}
									id={item._id}
									banner={item}
									index={index}
								/>
							))}
						</SortableContext>
					</DndContext>
				</div>

				<Button
					{...primaryDashboardButtonStyles}
					className='submit-button'
					onClick={onSaveOrder}
				>
					Salvar
				</Button>
			</Container>
		</Modal>
	)
}

export default ModalOrderBanner
